
import React, { useState, useEffect } from "react";
import { server } from "../common";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const AgniveerList = () => {
    const [registrations, setRegistrations] = useState([]);
    const [filters, setFilters] = useState({
        district: "",
        state: "",
        ageFrom: "",
        ageTo: "",
        fromDate: "",
        toDate: "",
    });
    const [loading, setLoading] = useState(false);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        fetchRegistrations(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const fetchRegistrations = (page) => {
        setLoading(true);
        server
            .get("/agniveer/all", {
                params: {
                    ...filters,
                    page: page,
                    limit: itemsPerPage
                }
            })
            .then((res) => {
                setRegistrations(res.data.data);
                const totalRecords = res.data.total;
                setTotalPages(Math.ceil(totalRecords / itemsPerPage));
            })
            .catch((err) => {
                console.error("Error fetching registrations:", err);
                toast.error("Failed to fetch registrations");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const applyFilters = () => {
        setCurrentPage(1);
        fetchRegistrations(1);
    };

    const clearFilters = () => {
        setFilters({ district: "", state: "", ageFrom: "", ageTo: "", fromDate: "", toDate: "" });
        setLoading(true);
        server
            .get("/agniveer/all", { params: { page: 1, limit: itemsPerPage } })
            .then((res) => {
                setRegistrations(res.data.data);
                const totalRecords = res.data.total;
                setTotalPages(Math.ceil(totalRecords / itemsPerPage));
                setFilters({ district: "", state: "", ageFrom: "", ageTo: "", fromDate: "", toDate: "" });
                setCurrentPage(1);
            })
            .catch((err) => {
                console.error("Error fetching registrations:", err);
                toast.error("Failed to fetch registrations");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const exportToExcel = () => {
        setLoading(true);
        // Fetch all data matching current filters
        server.get("/agniveer/all", {
            params: {
                ...filters,
                exportData: 'true'
            }
        })
            .then((res) => {
                const allRegistrations = res.data.data;

                if (!allRegistrations || allRegistrations.length === 0) {
                    toast.warn("No data to export");
                    setLoading(false);
                    return;
                }

                // Prepare data for export
                const dataToExport = allRegistrations.map(reg => ({
                    "ID": reg.id,
                    "Full Name": reg.fullName,
                    "Gender": reg.gender,
                    "Age": reg.age,
                    "Date of Birth": reg.dob || "-",
                    "Height": reg.height,
                    "Weight": reg.weight,
                    "Chest": reg.chest,
                    "Qualification": reg.qualification,
                    "Mobile": reg.mobile,
                    "WhatsApp Number": reg.mobileW || "-",
                    "Address": reg.address,
                    "District": reg.district,
                    "State": reg.state,
                    "Pin Code": reg.pincode,
                    "Date": reg.created_at ? new Date(reg.created_at).toLocaleDateString() : "-"
                }));

                const worksheet = XLSX.utils.json_to_sheet(dataToExport);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Agniveer Registrations");
                XLSX.writeFile(workbook, "Agniveer_Registrations.xlsx");
                toast.success(`Exported ${allRegistrations.length} records successfully!`);
            })
            .catch((err) => {
                console.error("Error exporting data:", err);
                toast.error("Failed to export data");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Pagination Logic
    // Pagination Logic updated to use server-side data directly
    // const indexOfLastItem = currentPage * itemsPerPage;
    // const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    // const currentItems = registrations; // registrations now only contains current page data
    // const totalPages is now state managed

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="container mt-5 mb-5">
            <h2 className="mb-4 fw-bold text-center">Agniveer Registrations</h2>

            <div className="card mb-4 shadow-sm">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title mb-0">Filter Registrations</h5>
                        <button className="btn btn-success fw-bold" onClick={exportToExcel} style={{ backgroundColor: "#1d740c", borderColor: "#1d740c" }}>
                            <i className="fa fa-file-excel me-2"></i>Export to Excel
                        </button>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <input
                                type="text"
                                name="district"
                                className="form-control"
                                placeholder="District"
                                value={filters.district}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <input
                                type="text"
                                name="state"
                                className="form-control"
                                placeholder="State"
                                value={filters.state}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2">
                            <input
                                type="number"
                                name="ageFrom"
                                className="form-control"
                                placeholder="Age From"
                                value={filters.ageFrom}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2">
                            <input
                                type="number"
                                name="ageTo"
                                className="form-control"
                                placeholder="Age To"
                                value={filters.ageTo}
                                onChange={handleFilterChange}
                            />
                        </div>

                        <div className="col-md-3">
                            <label className="form-label small text-muted mb-1">From Date</label>
                            <input
                                type="date"
                                name="fromDate"
                                className="form-control"
                                value={filters.fromDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small text-muted mb-1">To Date</label>
                            <input
                                type="date"
                                name="toDate"
                                className="form-control"
                                value={filters.toDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-6 d-flex gap-2 align-items-end">
                            <button className="btn btn-primary w-50" onClick={applyFilters}>
                                Apply
                            </button>
                            <button className="btn btn-secondary w-50" onClick={clearFilters}>
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-striped table-hover table-bordered align-middle">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>ID</th>
                                            <th>Full Name</th>
                                            <th>Gender</th>
                                            <th>Age</th>
                                            <th>Date of Birth</th>
                                            <th>Height</th>
                                            <th>Weight</th>
                                            <th>Chest</th>
                                            <th>Qualification</th>
                                            <th>Mobile</th>
                                            <th>WhatsApp</th>
                                            <th>District</th>
                                            <th>State</th>
                                            <th>Address</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {registrations.length > 0 ? (
                                            registrations.map((reg) => (
                                                <tr key={reg.id}>
                                                    <td>{reg.id}</td>
                                                    <td className="fw-bold">{reg.fullName}</td>
                                                    <td>{reg.gender}</td>
                                                    <td>{reg.age}</td>
                                                    <td>{reg.dob ? new Date(reg.dob).toLocaleDateString('en-GB') : "-"}</td>
                                                    <td>{reg.height}</td>
                                                    <td>{reg.weight}</td>
                                                    <td>{reg.chest}</td>
                                                    <td>{reg.qualification}</td>
                                                    <td>{reg.mobile}</td>
                                                    <td>{reg.mobileW || "-"}</td>
                                                    <td>{reg.district}</td>
                                                    <td>{reg.state}</td>
                                                    <td style={{ minWidth: "200px" }}>{reg.address}</td>
                                                    <td>{reg.created_at ? new Date(reg.created_at).toLocaleDateString() : "-"}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="15" className="text-center py-4">
                                                    No registrations found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <nav className="mt-4 d-flex justify-content-center">
                                    <ul className="pagination">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
                                        </li>
                                        {[...Array(totalPages)].map((_, i) => (
                                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => paginate(i + 1)}>
                                                    {i + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
                                        </li>
                                    </ul>
                                </nav>
                            )}

                            <div className="text-muted text-center mt-2 small">
                                Page {currentPage} of {totalPages}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div >
    );
};

export default AgniveerList;
